import { IFile } from '@repo/toolkit/types'
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult
} from '@tanstack/react-query'
import { useCallback } from 'react'

import { getCenterCostsView } from '../../../../api/centers/get-center-costs-view'
import { restApiClient } from '../../../../api/rest-api-client'
import { Files, FilesType } from '../../../../lib/constants/files'
import { CenterCostsView } from '../../../../lib/types/models/center/center-costs-view'
import { uploadFile } from '../../../../lib/utils/functions/file.functions'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { CenterCostType } from './utils'

/**
 * table types for the center costs hook
 * BasicTableType: will only return the center and center costs view
 * FullTableType: will return the center, center costs view, lock and unlock mutations
 */
type BasicTableType = 'costs'
type FullTableType =
  | 'activities'
  | 'cultural-outings'
  | 'host-family'
  | 'local-organizers'
  | 'miscellaneous'
  | 'teaching-hall'
  | 'tefl'
  | 'transfers'
  | 'budget'

type TableType = BasicTableType | FullTableType

type CenterCostsHookProps<T extends TableType> = {
  centerId: number
  table: T
  costType: CenterCostType
}

type UploadInvoiceInput = {
  id: number
  file: IFile
}

/**
 * CenterTableHook: will return the center, center costs view, lock and unlock mutations
 */
type CenterTableHook = {
  centerCostsView: UseQueryResult<CenterCostsView, Error>
  lock: UseMutationResult<void, Error, void>
  unlock: UseMutationResult<void, Error, void>
  isPending: boolean
  uploadInvoice: UseMutationResult<void, Error, UploadInvoiceInput>
  deleteInvoice: UseMutationResult<void, Error, number>
  openCenter: UseMutationResult<void, Error, void>
  validateCenter: UseMutationResult<void, Error, void>
}

/**
 * CenterCostsStatusHook: will only return the center and center costs view
 */
type CenterCostsStatusHook = Pick<
  CenterTableHook,
  'centerCostsView' | 'openCenter' | 'validateCenter' | 'isPending'
>

const tableApiMapping: Record<TableType, string> = {
  activities: 'activities',
  costs: 'costs-status',
  'cultural-outings': 'cultural-outings',
  'host-family': 'host-family-costs',
  'local-organizers': 'local-organizers',
  miscellaneous: 'exceptional-expenses',
  'teaching-hall': 'teaching-hall-costs',
  tefl: 'tefl-costs',
  transfers: 'arrival-departure-transfers',
  budget: 'budgets'
}

const uploadInvoiceMapping: Record<TableType, FilesType> = {
  activities: Files.CENTER_ACTIVITY_INVOICE,
  costs: Files.CENTER_TEFL_COSTS_INVOICE,
  'cultural-outings': Files.CENTER_CULTURAL_OUTING_INVOICE,
  'host-family': '',
  'local-organizers': '',
  miscellaneous: Files.CENTER_EXCEPTIONAL_EXPENSES_INVOICE,
  'teaching-hall': Files.CENTER_TEACHING_HALL_COSTS_INVOICE,
  tefl: Files.CENTER_TEFL_COSTS_INVOICE,
  transfers: Files.CENTER_ARRIVAL_DEPARTURE_TRANSFER_INVOICE,
  budget: Files.CENTER_BUDGET_INVOICE
}

const openCenterMapping: Record<CenterCostType, { open: string; validate: string }> = {
  'final-cost': { open: 'open-final-costs', validate: 'validate-final-costs' },
  'original-cost': { open: 'open-costs', validate: 'validate-costs' }
}

/**
 * useCenterCosts: will return the center, center costs view, lock and unlock mutations
 */
export function useCenterCosts(props: CenterCostsHookProps<FullTableType>): CenterTableHook

/**
 * useCenterCosts: will only return the center and center costs view
 */
export function useCenterCosts(props: CenterCostsHookProps<BasicTableType>): CenterCostsStatusHook

/**
 * useCenterCosts: will return the center, center costs view, lock and unlock mutations or only the center and center costs view
 */
export function useCenterCosts(props: CenterCostsHookProps<TableType>) {
  const { centerId, table, costType } = props
  const queryClient = useQueryClient()

  const centerCostsViewQuery = useQuery({
    queryKey: ['center-costs-view', centerId],
    queryFn: () => getCenterCostsView(centerId)
  })

  // Tables refetch this view after saving costs; refresh the grand total with it
  // so the totals and balances stay in sync without a page refresh
  const refetchCenterCostsView = useCallback<(typeof centerCostsViewQuery)['refetch']>(
    async (options) => {
      const result = await centerCostsViewQuery.refetch(options)
      await queryClient.invalidateQueries({ queryKey: ['center-grand-total-cost', centerId] })
      return result
    },
    [centerCostsViewQuery.refetch, queryClient, centerId]
  )

  const centerCostsView: typeof centerCostsViewQuery = {
    // eslint-disable-next-line @tanstack/query/no-rest-destructuring
    ...centerCostsViewQuery,
    refetch: refetchCenterCostsView
  }

  const openCenter = useMutation({
    mutationFn: () =>
      restApiClient.url(`/centers/${centerId}/${openCenterMapping[costType].open}`).post(),
    onError: showAxiosError
  })

  const validateCenter = useMutation({
    mutationFn: () =>
      restApiClient.url(`/centers/${centerId}/${openCenterMapping[costType].validate}`).post(),
    onError: showAxiosError
  })

  const lock = useMutation({
    mutationFn: () =>
      restApiClient.url(`/centers/${centerId}/${tableApiMapping[table]}/lock`).post(),
    onError: showAxiosError
  })

  const unlock = useMutation({
    mutationFn: () =>
      restApiClient.url(`/centers/${centerId}/${tableApiMapping[table]}/unlock`).post(),
    onError: showAxiosError
  })

  const uploadInvoice = useMutation({
    mutationFn: async (data: UploadInvoiceInput) => {
      const uploadedFile = await uploadFile(uploadInvoiceMapping[table], data.file)
      if (uploadedFile) data.file = uploadedFile
      else throw new Error('File not uploaded')

      const url =
        table === 'budget'
          ? `/centers/${centerId}/${tableApiMapping[table]}/upload-invoice`
          : `/centers/${centerId}/${tableApiMapping[table]}/${data.id}/upload-invoice`

      return restApiClient.url(url).post({ file: data.file })
    },
    onError: showAxiosError
  })

  const deleteInvoice = useMutation({
    mutationFn: async (id: number) => {
      const url =
        table === 'budget'
          ? `/centers/${centerId}/${tableApiMapping[table]}/delete-invoice`
          : `/centers/${centerId}/${tableApiMapping[table]}/${id}/delete-invoice`

      return restApiClient.url(url).delete()
    },
    onError: showAxiosError
  })

  const isPending =
    centerCostsView.isPending ||
    lock.isPending ||
    unlock.isPending ||
    uploadInvoice.isPending ||
    deleteInvoice.isPending ||
    openCenter.isPending ||
    validateCenter.isPending

  if (table === 'costs') return { centerCostsView, openCenter, validateCenter, isPending }
  return {
    centerCostsView,
    lock,
    unlock,
    isPending,
    uploadInvoice,
    deleteInvoice,
    openCenter,
    validateCenter
  }
}
